import { createContext, useContext } from 'react';

import type { StorefrontProductSummary } from '@/features/storefront/api/storefront';
import { swatchFor } from '@/features/storefront/layouts/product/options';
import { StorefrontThemeContext } from '@/features/storefront/storefrontThemeContext';
import { themeCards, type ThemeCards } from '@/features/storefront/themeSchema';

/**
 * The theme's product card settings (`ThemeConfig.cards`), as a card reads them.
 *
 * A card reads the shell's theme by default - the live shop and a theme
 * preview both provide one. The customiser's canvas has no shell, only the
 * draft it is editing, so it provides this context directly; `inert` there
 * keeps a quick add in the preview from putting anything in a real basket.
 * Outside both (a test, a dashboard thumbnail) every setting is off, which is
 * exactly the card that existed before these settings did.
 */
export interface ProductCardSettings {
  cards: ThemeCards;
  inert: boolean;
}

export const ProductCardSettingsContext = createContext<ProductCardSettings | null>(null);

export function useProductCardSettings(): ProductCardSettings {
  const provided = useContext(ProductCardSettingsContext);
  const shellTheme = useContext(StorefrontThemeContext);
  return provided ?? { cards: themeCards(shellTheme), inert: false };
}

/** How many colour dots a card shows before "+n". */
export const CARD_SWATCH_MAX = 5;

/** The badges a card can wear, each named for its shop-content key. */
export type CardBadge = 'sold_out' | 'sale' | 'new' | 'bestseller';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Which badges `product` wears under `cards`, most important first, at most two.
 *
 * Sold out says everything that matters on its own, so it stands alone. A
 * sale is a compare-at price above the price; new is added within the
 * theme's window of days; bestseller is the server's top-sellers flag.
 */
export function cardBadges(
  product: StorefrontProductSummary,
  cards: ThemeCards,
  now: number = Date.now(),
): CardBadge[] {
  const { badges } = cards;
  if (badges.sold_out && !product.in_stock) return ['sold_out'];
  const wearing: CardBadge[] = [];
  if (badges.sale && product.compare_at_price && Number(product.compare_at_price) > Number(product.price)) {
    wearing.push('sale');
  }
  if (badges.new && product.created_at) {
    const added = Date.parse(product.created_at);
    if (Number.isFinite(added) && now - added <= badges.new_days * DAY_MS) wearing.push('new');
  }
  if (badges.bestseller && product.is_bestseller) wearing.push('bestseller');
  return wearing.slice(0, 2);
}

/**
 * Plain colour words a merchant names variants in, for the card's dots.
 *
 * The product page's own list (`swatchFor`) is fabrics and finishes - sage,
 * walnut, oat - and deliberately leaves the primaries out, because there a
 * word it does not know turns a whole axis back into buttons. A card's dot is
 * one value at a time, so it can afford the everyday words too; anything
 * neither list knows is shown as a small text chip instead.
 */
const CARD_COLOURS: Record<string, string> = {
  red: '#d32f2f',
  maroon: '#7b1e2b',
  crimson: '#b0122f',
  pink: '#ec7fa9',
  magenta: '#c2185b',
  orange: '#f57c00',
  peach: '#f6b99b',
  yellow: '#f5c518',
  mustard: '#d4a017',
  gold: '#c9a227',
  green: '#2e7d32',
  mint: '#9fd8c0',
  lime: '#9ccc3c',
  emerald: '#1f8a5b',
  blue: '#1e63c4',
  sky: '#7cc0ec',
  turquoise: '#1fb5ad',
  indigo: '#3f3d8f',
  purple: '#6a3d9a',
  violet: '#7f4fc9',
  lavender: '#b9a5dd',
  wine: '#6d1a36',
  brown: '#6d4c41',
  tan: '#c9a77c',
  beige: '#e6d5b8',
  khaki: '#b8a77a',
  silver: '#c0c0c0',
  ivory: '#f6f1e3',
  offwhite: '#f4f1ea',
};

/** The colour a card's dot for `name` is painted, or undefined for a text chip. */
export function cardSwatchColour(name: string): string | undefined {
  // A word at a time, first hit wins across both lists - "Navy Blue" is navy.
  for (const word of name.toLowerCase().split(/[^a-z]+/)) {
    if (!word) continue;
    const hit = swatchFor(word) ?? CARD_COLOURS[word];
    if (hit) return hit;
  }
  return undefined;
}
